import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Heart, CalendarDays, Trophy } from "lucide-react";
import { api } from "@/lib/api";

const monthName = (m: string) => new Date(`${m}-01T12:00:00`).toLocaleDateString("en-AU", { month: "long", year: "numeric" });

let cache: Promise<any> | null = null;
function useGiveaway() {
  const [g, setG] = useState<any>(null);
  useEffect(() => {
    cache = cache || api.get("/giveaway").then((r) => r.data).catch(() => null);
    cache.then(setG);
    return () => { cache = null; };
  }, []);
  return g;
}

export function WinnerSpotlight({ at }: { at: "below_hero" | "above_instagram" }) {
  const g = useGiveaway();
  if (!g || !g.enabled || g.spotlight !== at) return null;
  const w = g.winners[0];
  const draw = new Date(`${g.next_draw}T12:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "long" });
  return (
    <section className="max-w-[1200px] mx-auto px-5 md:px-12 py-14 md:py-20" data-testid={`winner-spotlight-${at}`}>
      <div className="grid md:grid-cols-[minmax(0,380px)_1fr] gap-8 md:gap-14 items-center border border-neutral-200 bg-white p-6 md:p-10">
        {w?.image ? <img src={w.image} alt={w.name} className="w-full aspect-square object-cover" data-testid="winner-spotlight-image" />
          : <div className="w-full aspect-square bg-neutral-100 flex items-center justify-center text-[var(--c-primary)]"><Trophy size={56} strokeWidth={1} /></div>}
        <div>
          <p className="eyebrow text-[var(--c-primary)] mb-4">{w ? `${monthName(w.month)} Giveaway Winner` : "Monthly Instagram Giveaway"}</p>
          <h2 className="font-display text-3xl md:text-5xl font-light leading-tight" data-testid="winner-spotlight-name">{w ? `Congratulations, ${w.name}` : "Our first winner is coming soon"}</h2>
          {w?.ring && <p className="mt-3 text-neutral-600" data-testid="winner-spotlight-ring">{w.ring}</p>}
          {w?.note && <p className="mt-5 font-display text-xl italic text-neutral-700 leading-relaxed" data-testid="winner-spotlight-note">“{w.note}”</p>}
          <p className="mt-6 text-sm text-neutral-600">Follow us on Instagram for your chance to win a {g.prize}. Next draw {draw}.</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href={g.instagram_url} target="_blank" rel="noopener noreferrer" className="btn-ink inline-flex items-center gap-2 px-7 py-3.5 text-xs tracking-[0.22em] uppercase" data-testid="winner-spotlight-enter"><Instagram size={14} /> Enter on Instagram</a>
            <Link to="/pages/giveaway" className="btn-line px-7 py-3.5 text-xs tracking-[0.22em] uppercase" data-testid="winner-spotlight-all">See All Winners</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Countdown({ date }: { date: string }) {
  const d = new Date(`${date}T12:00:00`);
  const today = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Sydney" }));
  const days = Math.max(0, Math.round((Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000));
  return (
    <p className="inline-flex items-center gap-2 border border-[var(--c-primary)]/40 px-4 py-2 text-xs tracking-[0.18em] uppercase" data-testid="giveaway-next-draw">
      <CalendarDays size={14} /> Next draw {d.toLocaleDateString("en-AU", { day: "numeric", month: "long" })} · {days === 0 ? "today" : `${days} day${days === 1 ? "" : "s"} to go`}
    </p>
  );
}

const STEPS = [[Instagram, "Follow Somnio.Co", "Follow our Instagram account."], [Heart, "Like the giveaway post", "Like our monthly giveaway post to enter that month's draw."], [Trophy, "Win on the 30th", "One winner is drawn at random each month and posted here."]] as const;

export function GiveawayBlock({ b }: any) {
  const g = useGiveaway();
  if (!g) return null;
  return (
    <section className="max-w-[1200px] mx-auto px-5 md:px-12 py-16 md:py-24" data-testid="giveaway-block">
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-14 items-start">
        <div>
          <p className="eyebrow text-[var(--c-primary)] mb-5">Monthly Instagram Giveaway</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05]" data-testid="giveaway-heading">{b.heading}</h1>
          {b.intro && <p className="mt-6 text-neutral-600 leading-relaxed max-w-xl" data-testid="giveaway-intro">{b.intro}</p>}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a href={g.instagram_url} target="_blank" rel="noopener noreferrer" className="btn-ink inline-flex items-center gap-2 px-8 py-4 text-xs tracking-[0.25em] uppercase" data-testid="giveaway-instagram-link"><Instagram size={15} /> Enter on Instagram</a>
            <Countdown date={g.next_draw} />
          </div>
          {!g.enabled && <p className="mt-4 text-sm text-neutral-500" data-testid="giveaway-paused">The giveaway is taking a short break — follow us to hear when it's back.</p>}
        </div>
        <ol className="space-y-4">{STEPS.map(([I, t, d], i) => (
          <li key={t} className="flex gap-5 border border-neutral-200 bg-white p-6 transition-transform duration-300 hover:-translate-y-0.5" data-testid={`giveaway-step-${i}`}>
            <span className="w-11 h-11 shrink-0 rounded-full border border-[var(--c-primary)]/50 flex items-center justify-center text-[var(--c-primary)]"><I size={18} /></span>
            <div><p className="font-display text-xl">{i + 1}. {t}</p><p className="text-sm text-neutral-600 mt-1">{d}</p></div>
          </li>))}
          <li className="text-sm text-neutral-600 px-1">Prize: {g.prize}. <Link to="/pages/giveaway-terms" className="underline" data-testid="giveaway-terms-link">Terms & conditions</Link></li>
        </ol>
      </div>
      <div className="mt-20" data-testid="giveaway-winners">
        <h2 className="font-display text-3xl md:text-4xl font-light mb-8">Our Winners</h2>
        {!g.winners.length && <p className="text-neutral-500" data-testid="giveaway-no-winners">Our first winner will be announced here on {new Date(`${g.next_draw}T12:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}.</p>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{g.winners.map((w: any, i: number) => (
          <article key={`${w.month}-${i}`} className="border border-neutral-200 bg-white" data-testid={`giveaway-winner-${i}`}>
            {w.image && <img src={w.image} alt={w.name} className="w-full aspect-square object-cover" />}
            <div className="p-5"><p className="eyebrow text-[10px] text-[var(--c-primary)]">{monthName(w.month)}</p><p className="font-display text-2xl mt-2">{w.name}</p>
              {w.ring && <p className="text-sm text-neutral-600 mt-1">{w.ring}</p>}{w.note && <p className="text-sm text-neutral-500 mt-2 italic">“{w.note}”</p>}</div>
          </article>))}</div>
      </div>
    </section>
  );
}
